import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchHolidays } from "../../../redux/slices/absenceSlice";
import { FaCalendarPlus } from "react-icons/fa";
import YearlyCalendar from "../../calendar/YearlyCalendar/YearlyCalendar";
import "../../calendar/YearlyCalendar/YearlyCalendar.css";

const UserAbsenceModal = ({
  show,
  handleClose,
  userProfile,
  currentUserId,
  pendingAbsence,
  isAdmin,
  onAccept,
  onReject,
  onDelete,
  onAddAbsenceClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [year, setYear] = useState(new Date().getFullYear());
  const isOwner = userProfile?.id === currentUserId;
  const [modalHolidays, setModalHolidays] = useState([]);

  useEffect(() => {
    if (show) {
      dispatch(fetchHolidays(year))
        .unwrap()
        .then((data) => setModalHolidays(data))
        .catch((_err) => console.error("Erro feriado"));
    }
  }, [year, show, dispatch]);

  if (!userProfile) return null;

  const MINUTES_PER_DAY = 480;
  const remainingDays = userProfile?.available_minutes
    ? Math.round((userProfile.available_minutes / MINUTES_PER_DAY) * 10) / 10
    : 0;

  const sortedAbsences = [...(userProfile.absences || [])].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date),
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const targetId =
    typeof pendingAbsence === "object" ? pendingAbsence?.id : pendingAbsence;
  const absenceToShow =
    sortedAbsences.find((a) => String(a.id) === String(targetId)) ||
    sortedAbsences[0];

  const firstName = userProfile.first_name || "";
  const lastName = userProfile.last_name || "";
  const fullName =
    `${firstName} ${lastName}`.trim() || userProfile.display_name || t("components.UserAbsenceModal.unknown");

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      contentClassName="border-0 shadow-lg"
      style={{ borderRadius: "var(--border-radius-lg)" }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-3 w-100">
          {userProfile.profile_picture ? (
            <img
              src={userProfile.profile_picture}
              alt="Profile"
              className="rounded-circle object-fit-cover"
              style={{
                width: 55,
                height: 55,
                border: "2px solid var(--brand-primary)",
              }}
            />
          ) : (
            <div
              className="rounded-circle d-flex justify-content-center align-items-center txt-white fw-bold"
              style={{ width: 55, height: 55, backgroundColor: "#f0f0f081", border: "1px solid var(--brand-primary)" }}
            >
              {firstName[0]}
              {lastName[0]}
            </div>
          )}

          <div>
            <h5
              className="m-0 fw-bold"
              style={{ color: "var(--brand-primary)" }}
            >
              {fullName}
            </h5>
            <small
              className="fw-bold"
              style={{ color: "var(--action-color)", fontSize: "14px" }}
            >
              {t("components.UserAbsenceModal.remaining_days", { count: remainingDays })}
            </small>
          </div>

          {isAdmin && (
            <Button
              variant="success"
              className="d-flex align-items-center gap-2"
              style={{
                fontSize: "12px",
                padding: "5px 12px",
                borderRadius: "20px",
                backgroundColor: "#10b981",
                border: "none",
                marginRight: "20px",
                marginLeft: "auto",
              }}
              onClick={() => onAddAbsenceClick(userProfile.id)}
            >
              <FaCalendarPlus size={12} />
              {t("components.UserAbsenceModal.add")}
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <Row className="g-4">
          <Col lg={4}>
            <div
              className="dashboard-card p-4 h-100 border-0 rounded-4 d-flex flex-column"
              style={{
                backgroundColor: "var(--bg-main)",
                boxShadow: "inset 0 0 10px rgba(0,0,0,0.02)",
              }}
            >
              <h6
                className="mb-4 fw-bold fs-5"
                style={{ color: "var(--text-primary)" }}
              >
                {t("components.UserAbsenceModal.request_details")}
              </h6>

              {absenceToShow ? (
                <div
                  className="d-flex flex-column gap-3 fs-6 flex-grow-1"
                  style={{ color: "var(--text-primary)" }}
                >
                  <div>
                    <span className="fw-bold opacity-75">{t("components.UserAbsenceModal.type")}:</span>{" "}
                    <span className="ms-1">{absenceToShow.absence_type}</span>
                  </div>
                  <div>
                    <span className="fw-bold opacity-75">{t("components.UserAbsenceModal.start")}:</span>{" "}
                    <span className="ms-1">
                      {formatDate(absenceToShow.start_date)}
                    </span>
                  </div>
                  <div>
                    <span className="fw-bold opacity-75">{t("components.UserAbsenceModal.end")}:</span>{" "}
                    <span className="ms-1">
                      {formatDate(absenceToShow.end_date)}
                    </span>
                  </div>

                  <div className="mt-2 flex-grow-1">
                    <span className="fw-bold d-block mb-2 opacity-75">
                      {t("components.UserAbsenceModal.justification")}:
                    </span>
                    <p
                      className="p-3 rounded-3"
                      style={{
                        backgroundColor: "var(--bg-surface)",
                        border: "var(--border-thin)",
                        fontSize: "14px",
                      }}
                    >
                      {absenceToShow.reason || t("components.UserAbsenceModal.no_remarks")}
                    </p>
                  </div>

                  <div className="d-flex flex-column gap-2 mt-4">
                    {isAdmin && (
                      <div className="d-flex flex-column gap-2">
                        {absenceToShow.status?.toLowerCase() === "pending" && (
                          <div className="d-flex gap-2">
                            <Button
                              className="flex-fill fw-bold"
                              style={{
                                padding: "12px 0",
                                borderRadius: "10px",
                                backgroundColor: "var(--danger-red)",
                                border: "none",
                              }}
                              onClick={() => onReject(absenceToShow.id)}
                            >
                              {t("components.UserAbsenceModal.reject")}
                            </Button>
                            <Button
                              className="flex-fill fw-bold"
                              style={{
                                padding: "12px 0",
                                borderRadius: "10px",
                                backgroundColor: "#10b981",
                                border: "none",
                              }}
                              onClick={() => onAccept(absenceToShow.id)}
                            >
                              {t("components.UserAbsenceModal.accept")}
                            </Button>
                          </div>
                        )}

                        {absenceToShow.status?.toLowerCase() === "accepted" && (
                          <Button
                            variant="warning"
                            className="w-100 fw-bold"
                            style={{
                              padding: "12px 0",
                              borderRadius: "10px",
                              color: "#92400e",
                            }}
                            onClick={() => onReject(absenceToShow.id)}
                          >
                            {t("components.UserAbsenceModal.revoke_approval")}
                          </Button>
                        )}

                        {absenceToShow.status?.toLowerCase() === "rejected" && (
                          <Button
                            className="w-100 fw-bold"
                            style={{
                              padding: "12px 0",
                              borderRadius: "10px",
                              backgroundColor: "#10b981",
                              border: "none",
                            }}
                            onClick={() => onAccept(absenceToShow.id)}
                          >
                            {t("components.UserAbsenceModal.accept_request")}
                          </Button>
                        )}
                      </div>
                    )}

                    {isOwner &&
                      absenceToShow.status?.toLowerCase() === "pending" && (
                        <Button
                          variant="outline-danger"
                          className="w-100 fw-bold"
                          style={{ padding: "12px 0", borderRadius: "10px" }}
                          onClick={() => onDelete(absenceToShow.id)}
                        >
                          {t("components.UserAbsenceModal.cancel_request")}
                        </Button>
                      )}
                  </div>
                </div>
              ) : (
                <p className="text-muted">{t("components.UserAbsenceModal.no_requests")}</p>
              )}
            </div>
          </Col>

          <Col lg={8}>
            <div
              className="dashboard-card border-0 rounded-4 modal-calendar p-2"
              style={{ backgroundColor: "var(--bg-surface)" }}
            >
              <YearlyCalendar
                absences={userProfile.absences || []}
                holidays={modalHolidays}
                currentYear={year}
                onYearChange={setYear}
              />
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default UserAbsenceModal;